import { possessive } from '@shared';
import { Button, Card } from '@design-system';
import { BookACallButton } from './book-a-call-button';
import { CaseDetailsField } from './case-details-field';

import { ClipboardCheck, PhoneCall, Sparkles } from 'lucide-react';

import type { ReactNode } from 'react';

type HandoffVariant = 'licensed' | 'consult';

interface LicensedDoneProps {
  clientFirstName: string;
  clientId?: string;
  variant?: HandoffVariant;
  onClose?: () => void;
  closeLabel?: string;
}

interface NextStep {
  icon: ReactNode;
  title: string;
  description: string;
}

function getCopy(variant: HandoffVariant, clientFirstName: string) {
  if (variant === 'consult') {
    return {
      subtitle: `A Waterlily team member will reach out to you today to consult on your options for ${clientFirstName}.`,
      steps: [
        {
          icon: <ClipboardCheck className="size-5" />,
          title: 'We review your goals',
          description: `Our team looks at what you're planning for ${clientFirstName}.`,
        },
        {
          icon: <PhoneCall className="size-5" />,
          title: 'We reach out today',
          description: 'A team member contacts you to talk through your options.',
        },
        {
          icon: <Sparkles className="size-5" />,
          title: 'We map out a path',
          description: `We help you plan for ${possessive(clientFirstName)} long-term care.`,
        },
      ] as NextStep[],
    };
  }

  return {
    subtitle: `Someone from our licensed support team will reach out to you today to discuss your planning goals for ${clientFirstName}.`,
    steps: [
      {
        icon: <ClipboardCheck className="size-5" />,
        title: 'We review your goals',
        description: `Our licensed team looks at what you're planning for ${clientFirstName}.`,
      },
      {
        icon: <PhoneCall className="size-5" />,
        title: 'We reach out today',
        description: 'A licensed expert contacts you to talk through your options.',
      },
      {
        icon: <Sparkles className="size-5" />,
        title: 'We build the plan together',
        description: `We tailor a long-term care plan to ${possessive(clientFirstName)} needs.`,
      },
    ] as NextStep[],
  };
}

export function LicensedDone({
  clientFirstName,
  clientId,
  variant = 'licensed',
  onClose,
  closeLabel = 'Back to clients',
}: LicensedDoneProps) {
  const { subtitle, steps } = getCopy(variant, clientFirstName);
  const showDetailsField = variant === 'licensed' && !!clientId;

  return (
    <div className="mx-auto w-full max-w-lg">
      <div className="flex flex-col items-center text-center">
        <h1 className="mt-6 text-balance text-3xl font-bold text-gray-950">You&apos;re all set</h1>
        <p className="text-foreground mt-2 text-balance">{subtitle}</p>
      </div>

      <Card className="mt-6 p-6 shadow-none">
        {steps.map(step => (
          <div
            key={step.title}
            className="flex items-start gap-4 text-start"
          >
            <span className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-md">
              {step.icon}
            </span>
            <div>
              <p className="font-medium text-gray-950">{step.title}</p>
              <p className="text-foreground text-sm">{step.description}</p>
            </div>
          </div>
        ))}
      </Card>

      {showDetailsField && (
        <div className="mt-6">
          <CaseDetailsField
            clientId={clientId}
            clientFirstName={clientFirstName}
          />
        </div>
      )}

      {onClose && (
        <Button
          size="lg"
          onClick={onClose}
          className="mt-6 w-full"
        >
          {closeLabel}
        </Button>
      )}

      <BookACallButton className="mt-3" />
    </div>
  );
}
